import { useDatabase } from "@nozbe/watermelondb/hooks";
import { useFocusEffect } from "@react-navigation/native";
import { StackScreenProps } from "@react-navigation/stack";
import {
  Button,
  DEFAULT_PAGE_WIDTH,
  Divider,
  FlatListSearchable,
  Page,
  Stack,
  Text,
  TextButton,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import React from "react";
import { Spacings, View } from "react-native-ui-lib";
import { FlatListSearchableSkeleton } from "../../../../components/FlatListSearchableSkeleton";
import { InvoiceAddProductModalRootScreenParamList } from "../../../../types";
import ProductModel from "../../../../utilities/data/model/Product";
import { useLoadMoreList } from "../../../../utilities/load-more-list";
import { useScheduler } from "../../../../utilities/scheduler";
import { useSearch } from "../../../../utilities/search";
import { QUERY_PRODUCT_LIST } from "../../../products/utils";
import { useInvoiceArrivalContextProvider } from "../../utils/active-invoice-arrival";
import { useSelectedProductContextProvider } from "../../utils/selected-product/hooks";
import { useTranslation } from "../../../../utilities/i18n";
import { ProductResourceListItem } from "../../../products/components/ProductResourceListItem";
import { ROAM_LOCATION_HEADER_KEY } from "../../../../constants";
import { useListFilterContext } from "../../../../utilities/list-filter/use-list-filter-context";
import { ProductsFilterValues } from "../../../products/types";
import { productFilterValuesToApiInput } from "../../../products/common";
import { SearchButton } from "../../../../components";
import { AntDesign } from "@expo/vector-icons";

export type SelectProductModalScreenProps =
  StackScreenProps<InvoiceAddProductModalRootScreenParamList>;

export const SelectProductModalScreen = ({
  navigation,
}: SelectProductModalScreenProps) => {
  const {
    appState: { locationId },
  } = useWithAppState();
  const { t } = useTranslation("invoice_arrival");
  const db = useDatabase();

  const { schedule } = useScheduler(500);
  const search = useSearch();
  const { invoiceArrival } = useInvoiceArrivalContextProvider();
  const { filterState, watch } = useListFilterContext<ProductsFilterValues>();
  const { activeFiltersCount } = filterState;
  const watchedFilterValues = watch();

  const { setProduct } = useSelectedProductContextProvider();

  const loadMoreList = useLoadMoreList<Product>({
    initialLoad: false,
    query: QUERY_PRODUCT_LIST,
    resultKey: "products",
    sort: { updatedAt: "DESC" },
    context: {
      headers: {
        [ROAM_LOCATION_HEADER_KEY]: locationId ?? undefined,
      },
    },
    variableFormatter: (variables) => {
      return {
        options: variables,
      };
    },
  });

  useFocusEffect(
    React.useCallback(() => {
      if (loadMoreList.called) {
        handleLoadMore(true);
      }
    }, [loadMoreList.called])
  );

  React.useEffect(() => {
    if (loadMoreList.called) {
      schedule(() => handleLoadMore(true));
    }
  }, [search.query]);

  useFocusEffect(
    React.useCallback(() => {
      navigation.setOptions({
        headerTitle() {
          return (
            <TextButton
              iconStyle={{ marginRight: 0 }}
              icon={<AntDesign name="filter" size={20} />}
              onPress={() => {
                navigation.navigate("ProductListFilterRootScreen", {
                  screen: "ProductListFilterScreen",
                });
              }}
            >
              {t("common:filter", {
                count: activeFiltersCount,
              })}
            </TextButton>
          );
        },
      });
    }, [activeFiltersCount])
  );

  const onAddNonMatchedProduct = React.useCallback(() => {
    navigation.navigate("AddNonProductModalScreen", {
      barcode: search.query,
    });
  }, [search.query]);

  const handleLoadMore = (reset?: boolean) => {
    loadMoreList.loadMore(
      {
        filter: {
          term: { contains: search.query },
          ...productFilterValuesToApiInput(watchedFilterValues),
        },
      },
      reset
    );
  };

  React.useEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <Button
          label={t("button.create_add")}
          isAction
          onPress={() =>
            navigation.navigate(
              "CreateProductRootScreen",
              //@ts-ignore
              {
                defaultValues: {
                  stockNumber: search.query,
                  locationId: invoiceArrival.locationId,
                  supplierId: invoiceArrival.supplierId,
                },
              }
            )
          }
          size="compact"
        ></Button>
      ),
    });
  }, [search.query]);

  const handleOnSelect = async (item: any) => {
    try {
      const loadedProduct = await db.collections
        .get<ProductModel>("products")
        .find(item.id);

      setProduct(loadedProduct);

      navigation.navigate("AddProductModalScreen", {
        productId: item.id,
      });
    } catch (err) {}
  };

  const items: Product[] = loadMoreList.items || [];

  const renderItem = React.useCallback(({ item }: { item: Product }) => {
    return (
      <ProductResourceListItem
        product={item}
        action={() => handleOnSelect(item)}
      />
    );
  }, []);

  const emptyComponentMarkup = (
    <View>
      <Text>{t("common:empty_list")}</Text>
    </View>
  );

  if (loadMoreList.loading && loadMoreList.called == false) {
    return (
      <Page>
        <FlatListSearchableSkeleton searchbar={true} />;
      </Page>
    );
  }

  const addNoneProductMarkup =
    !!search.query && search.query.length >= 1 ? (
      <View style={{ marginTop: Spacings.s5 }}>
        <SearchButton
          title={`Add Non Matched "${search.query}" Product`}
          secondaryTitle="Add a Product with the given barcode"
          onPress={onAddNonMatchedProduct}
        />
      </View>
    ) : undefined;

  return (
    <Page scrollable={false} padding={0} fullWidth>
      <FlatListSearchable
        contentContainerStyle={{
          maxWidth: DEFAULT_PAGE_WIDTH,
          width: "100%",
          alignSelf: "center",
          paddingHorizontal: Spacings.s5,
          paddingTop: Spacings.s5,
        }}
        placeholder={t("common:search2")}
        onChange={(query) => search.setQuery(query)}
        value={search.query}
        ItemSeparatorComponent={Divider}
        renderItem={renderItem}
        refreshing={loadMoreList.loading}
        data={items as any}
        scrollEnabled={true}
        ListEmptyComponent={emptyComponentMarkup}
        onEndReached={() => {
          handleLoadMore(false);
        }}
        renderSearchBarHeader={(searchBarMarkup, divider) => (
          <View>
            <View>
              {searchBarMarkup}
              {addNoneProductMarkup}
              {divider}
            </View>
          </View>
        )}
      />
    </Page>
  );
};
